// frontend/src/pages/DataWorkspacePage/AutomationsTab.jsx
import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../api/index.js';
import { DynamicIcon } from '../../components/icons.jsx';
import { notify } from '@/components/core';

const AutomationsTab = ({ token, connectionId, tableName }) => {
    const [rules, setRules] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [availableFlows, setAvailableFlows] = useState([]);
    
    // Schema fetch state for conditions autocomplete
    const [tableColumns, setTableColumns] = useState([]);

    useEffect(() => {
        if (!connectionId || !tableName) return;
        
        const loadDependencies = async () => {
            setIsLoading(true);
            try {
                // Fetch existing rules
                const rulesData = await apiFetch(`/data-workspace/rules/${connectionId}/${tableName}`, token);
                setRules(rulesData || []);
                
                // Fetch backend flows so users can select a target
                const flowsData = await apiFetch('/flows', token);
                setAvailableFlows(flowsData || []);

                // Fetch schema to provide column dropdowns
                const schemaData = await apiFetch(`/datasources/${connectionId}/structure`, token);
                if (schemaData && schemaData.tables) {
                    const tbl = schemaData.tables.find(t => t.name === tableName);
                    if (tbl) setTableColumns(tbl.columns);
                }
            } catch (err) {
                console.error("Failed to load automation dependencies:", err);
                notify.error("Failed to load automations");
            } finally {
                setIsLoading(false);
            }
        };

        loadDependencies();
    }, [connectionId, tableName, token]);

    const addRule = () => {
        const newRule = {
            id: 'draft_' + Date.now(),
            name: `Rule ${rules.length + 1}`,
            is_active: true,
            trigger_type: 'ON_UPDATE',
            conditions: [{ column: '', operator: '==', value: '' }],
            actions: { type: 'TRIGGER_FLOW', flow_id: '' },
            is_draft: true // Flag to know it's not saved yet
        };
        setRules([...rules, newRule]);
    };

    const saveRule = async (index) => {
        const rule = rules[index];
        try {
            // Validation
            if (!rule.name) throw new Error("Rule name is required");
            if (!rule.actions.flow_id) throw new Error("Target Flow is required");
            if (rule.conditions.some(c => !c.column || !c.value)) throw new Error("Condition fields cannot be empty");

            await apiFetch('/data-workspace/rules', token, {
                method: 'POST',
                body: JSON.stringify({
                    connection_id: connectionId,
                    table_name: tableName,
                    name: rule.name,
                    description: rule.description || "",
                    is_active: rule.is_active,
                    trigger_type: rule.trigger_type,
                    conditions: rule.conditions,
                    actions: rule.actions
                })
            });

            notify.success("Rule saved effectively!");
            
            // Re-fetch bounds
            const rulesData = await apiFetch(`/data-workspace/rules/${connectionId}/${tableName}`, token);
            setRules(rulesData || []);
        } catch (err) {
            notify.error(`Save failed: ${err.message}`);
        }
    };

    const deleteRule = async (index) => {
        const rule = rules[index];
        if (rule.is_draft) {
            const nextRules = [...rules];
            nextRules.splice(index, 1);
            setRules(nextRules);
            return;
        }

        try {
            await apiFetch(`/data-workspace/rules/${rule.id}`, token, { method: 'DELETE' });
            notify.success("Rule deleted");
            const nextRules = [...rules];
            nextRules.splice(index, 1);
            setRules(nextRules);
        } catch (err) {
            notify.error(`Delete failed: ${err.message}`);
        }
    };

    const updateRuleField = (ruleIndex, field, value) => {
        const nextRules = [...rules];
        nextRules[ruleIndex][field] = value;
        setRules(nextRules);
    };

    const updateCondition = (ruleIndex, condIndex, field, value) => {
        const nextRules = [...rules];
        nextRules[ruleIndex].conditions[condIndex][field] = value;
        setRules(nextRules);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full text-slate-500 gap-2">
                <DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> Loading Automations...
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <div>
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                        <DynamicIcon name="Zap" className="w-5 h-5 text-amber-500" />
                        Table Automations
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Map data modifications in <b className="text-slate-700">{tableName}</b> to trigger downstream Business Flows.
                    </p>
                </div>
                <button 
                    onClick={addRule}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-md text-sm font-semibold text-white shadow-sm flex items-center gap-2 transition"
                >
                    + New Rule
                </button>
            </div>

            {rules.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 bg-white border border-dashed border-slate-300 rounded-xl shadow-sm">
                    <DynamicIcon name="Activity" className="w-12 h-12 text-slate-400 mb-4" />
                    <h3 className="text-sm font-semibold text-slate-700">No Automations Defined</h3>
                    <p className="text-xs text-slate-500 mt-1 max-w-sm text-center">
                        Add a rule to automatically orchestrate backend flows when cells in this table are updated to specific values.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {rules.map((rule, idx) => (
                        <div key={rule.id} className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col space-y-4 shadow flex-1">
                            
                            {/* Header */}
                            <div className="flex justify-between items-center bg-slate-50 -mx-5 -mt-5 p-4 border-b border-slate-200 rounded-t-xl mb-2">
                                <div className="flex items-center gap-3 w-1/3">
                                    <input 
                                        type="checkbox"
                                        checked={rule.is_active}
                                        onChange={e => updateRuleField(idx, 'is_active', e.target.checked)}
                                        className="h-4 w-4 bg-white border-slate-300 rounded accent-amber-500"
                                        title="Enable / Disable Rule"
                                    />
                                    <input 
                                        value={rule.name}
                                        onChange={e => updateRuleField(idx, 'name', e.target.value)}
                                        placeholder="Rule Name"
                                        className="bg-transparent border-none text-base font-bold text-slate-800 focus:outline-none focus:ring-1 focus:ring-amber-500/50 rounded px-1"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    {rule.is_draft && (
                                        <button onClick={() => saveRule(idx)} className="px-3 py-1 bg-amber-50 text-amber-600 border border-amber-200 font-medium text-xs rounded hover:bg-amber-100">
                                            Save Rule
                                        </button>
                                    )}
                                    <button onClick={() => deleteRule(idx)} className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                                        <DynamicIcon name="Trash2" className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Trigger Event */}
                            <div className="flex items-center gap-4 text-sm">
                                <span className="font-semibold text-slate-500 w-24">IF EVENT</span>
                                <select 
                                    value={rule.trigger_type}
                                    onChange={e => updateRuleField(idx, 'trigger_type', e.target.value)}
                                    className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-800 focus:outline-none focus:border-blue-500"
                                >
                                    <option value="ON_UPDATE">Row Updated (ON_UPDATE)</option>
                                    <option value="ON_INSERT">Row Inserted (ON_INSERT)</option>
                                </select>
                            </div>

                            {/* Conditions */}
                            <div className="flex items-start gap-4 text-sm">
                                <span className="font-semibold text-slate-500 w-24 mt-2">MATCHING</span>
                                <div className="flex-1 space-y-2 relative border-l-2 border-slate-200 pl-4 ml-2">
                                    {rule.conditions.map((cond, cIdx) => (
                                        <div key={cIdx} className="flex gap-2 items-center bg-slate-50 p-2 rounded-lg border border-slate-200">
                                            <span className="text-xs font-mono text-slate-500 px-2">WHERE</span>
                                            <select
                                                value={cond.column}
                                                onChange={e => updateCondition(idx, cIdx, 'column', e.target.value)}
                                                className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-slate-800 w-1/3"
                                            >
                                                <option value="">Select Column...</option>
                                                {tableColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                                            </select>
                                            
                                            <select
                                                value={cond.operator}
                                                onChange={e => updateCondition(idx, cIdx, 'operator', e.target.value)}
                                                className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-amber-600 w-24 text-center font-mono font-bold"
                                            >
                                                <option value="==">==</option>
                                                <option value="!=">!=</option>
                                                <option value=">">&gt;</option>
                                                <option value="<">&lt;</option>
                                            </select>

                                            <input
                                                placeholder="Value"
                                                value={cond.value}
                                                onChange={e => updateCondition(idx, cIdx, 'value', e.target.value)}
                                                className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-800 flex-1 font-mono"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Action */}
                            <div className="flex items-center gap-4 text-sm pt-2 border-t border-slate-100 mt-4">
                                <span className="font-semibold text-emerald-600 w-24">THEN DO</span>
                                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 p-2 rounded-lg flex-1">
                                    <DynamicIcon name="PlayCircle" className="w-5 h-5 text-emerald-500" />
                                    <span className="text-emerald-700 font-semibold text-xs ml-1">TRIGGER FLOW:</span>
                                    <select
                                        value={rule.actions.flow_id || ''}
                                        onChange={e => {
                                            const nextRules = [...rules];
                                            nextRules[idx].actions = { ...nextRules[idx].actions, flow_id: e.target.value };
                                            setRules(nextRules);
                                        }}
                                        className="bg-white border border-emerald-300 rounded-md px-3 py-1.5 text-emerald-900 flex-1 ml-2 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 font-semibold"
                                    >
                                        <option value="">Select Target Flow to Execute...</option>
                                        {availableFlows.map(f => (
                                            <option key={f.id} value={f.id}>{f.name} {f.status === 'Published' ? `(v${f.version_number})` : '(Draft)'}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AutomationsTab;
